import { api } from "aldoo-ra/API";
import { SubmitSupportTicket } from "aldoo-ra/Admin/CustomerSupport/api";

// Create API client
const messageAPI = api("user-message");
const hcAPI = api("help-center");


/**
 * Get the complete help center table of contents with language support
 * This provides the entire structure for efficient client-side navigation
 * @param {string} [language] - Optional language code to override global setting
 * @returns {Promise<object>} - Complete help center structure
 */
export const GetHelpCenterTOC = async (language) => {
    const lang = language || getCurrentLanguage();
    return await hcAPI({ get_toc: { language: lang } });
};

/**
 * Get content by URL with language support
 * @param {string} contentURL - The URL path to resolve (e.g., "/category/helpcenterroot")
 * @param {string} [language] - Optional language code to override global setting
 * @returns {Promise<object>} - The resolved content object
 */
export const GetContent = async (contentURL, language) => {
    const lang = language || getCurrentLanguage();
    return await hcAPI({ get_content: { contentURL, language: lang } });
};

/**
 * Get root categories with language support
 * @param {string} [language] - Optional language code to override global setting
 * @returns {Promise<object>} - Root category content with subcategories
 */
export const GetRootCategories = async (language) => {
    const lang = language || getCurrentLanguage();
    return await hcAPI({ get_content: { contentURL: "/category/helpcenterroot", language: lang } });
};

/**
 * Get category by slug with language support
 * @param {string} slug - Category slug
 * @param {string} [language] - Optional language code to override global setting
 * @returns {Promise<object>} - Category content
 */
export const GetCategory = async (slug, language) => {
    const lang = language || getCurrentLanguage();
    return await hcAPI({ get_content: { contentURL: `/category/${slug}`, language: lang } });
};

/**
 * Get nested category by path with language support
 * @param {string[]} categoryPath - Array of category slugs from parent to child
 * @param {string} [language] - Optional language code to override global setting
 * @returns {Promise<object>} - Category content
 */
export const GetNestedCategory = async (categoryPath, language) => {
    const path = categoryPath.join('/');
    const lang = language || getCurrentLanguage();
    return await hcAPI({ get_content: { contentURL: `/category/${path}`, language: lang } });
};

/**
 * Get article by slug with language support
 * @param {string} slug - Article slug
 * @param {string} [language] - Optional language code to override global setting
 * @returns {Promise<object>} - Article content
 */
export const GetArticle = async (slug, language) => {
    const lang = language || getCurrentLanguage();
    return await hcAPI({ get_content: { contentURL: `/article/${slug}`, language: lang } });
};

/**
 * Get article in a specific category with language support
 * @param {string|string[]} categoryPath - Category path or array of category slugs
 * @param {string} articleSlug - Article slug
 * @param {string} [language] - Optional language code to override global setting
 * @returns {Promise<object>} - Article content
 */
export const GetArticleInCategory = async (categoryPath, articleSlug, language) => {
    // Handle array of category slugs
    if (Array.isArray(categoryPath)) {
        categoryPath = categoryPath.join('/');
    }
    // Ensure categoryPath doesn't start with a slash
    if (categoryPath.startsWith('/')) {
        categoryPath = categoryPath.substring(1);
    }
    const lang = language || getCurrentLanguage();
    return await hcAPI({ get_content: { contentURL: `/category/${categoryPath}/article/${articleSlug}`, language: lang } });
};

/**
 * Search for articles by term with language support
 * @param {string} term - Search term to look for in articles
 * @param {string} [language] - Optional language code to override global setting
 * @returns {Promise<object>} - Search results with matching articles and their categories
 */
export const SearchArticles = async (term, language) => {
    const lang = language || getCurrentLanguage();
    return await hcAPI({ search: { term, language: lang } });
};

/**
 * Send a help center request
 * @param {Object} payload - The request payload
 * @param {string} payload.email - User's email
 * @param {string} payload.product - Selected product
 * @param {string} payload.description - Request description
 * @param {File} [payload.file] - Optional attachment
 * @returns {Promise<object>} - API response
 */
export const SendHelpCenterRequest = async (payload) => {
    return await SubmitSupportTicket(payload)
};

/**
 * Send a user site contact message
 * @param {Object} payload - The contact payload
 * @returns {Promise<object>} - API response
 */
export const SendUserSiteContact = async (payload) => {
    // Add site_contact flag to payload
    payload = {
        ...payload,
        site_contact: true,
    };

    // Create FormData for file upload
    const formData = new FormData();
    for (const [key, value] of Object.entries(payload)) {
        formData.append(key, value);
    }

    // Send the request
    const response = await messageAPI(formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });

    return response.data;
};

/**
 * Safely gets the current language from localStorage
 * This avoids using React hooks at the module level
 * @returns {string} Current language code
 */
const getCurrentLanguage = () => {
    return "en"; // Force English for help center
    
    // Uncomment below to revert to using site language:
    // try {
    //     const storedValue = localStorage.getItem("current-site-language");
    //     return storedValue ? storedValue.replace(/\"/g, "") : "en";
    // } catch (error) {
    //     return "en";
    // }
};

export default {
    GetContent,
    GetRootCategories,
    GetCategory,
    GetNestedCategory,
    GetArticle,
    GetArticleInCategory,
    SearchArticles,
    SendHelpCenterRequest,
    SendUserSiteContact,
    GetHelpCenterTOC // Added new function
};